import { motion, useReducedMotion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import type { AskUserCardTheme } from './theme'

const segmentVariants: Variants = {
  empty: { scaleX: 0 },
  filled: { scaleX: 1 },
}
const segmentTransition: Transition = { duration: 0.3, ease: [0.23, 1, 0.32, 1] }
const segmentReducedMotionTransition: Transition = { duration: 0 }

/**
 * External step numbering for a card rendered as part of a queue of pending
 * cards (the chat output's floating bar): where this card's steps sit inside
 * the combined total, and whether more cards follow it — which keeps the
 * primary button on "Next" instead of the submit label.
 */
export interface AskUserCardProgress {
  offset: number
  total: number
  hasFollowingSteps: boolean
}

interface StepProgressProps {
  total: number
  current: number
  theme: AskUserCardTheme
  onSelect?: (index: number) => void
  isStepEnabled?: (index: number) => boolean
}

/** The `input` pattern's step progress bar (Figma "User Input Steps"). */
export function StepProgress({ total, current, theme, onSelect, isStepEnabled }: StepProgressProps) {
  const interactive = onSelect !== undefined
  const reducedMotion = useReducedMotion() ?? false

  return (
    <div
      role={interactive ? 'group' : undefined}
      aria-hidden={interactive ? undefined : true}
      aria-label={interactive ? 'Question progress' : undefined}
      className="flex w-full items-center gap-1"
    >
      {Array.from({ length: total }, (_, index) => {
        const enabled = interactive && (isStepEnabled?.(index) ?? true)
        const segment = (
          <span aria-hidden="true" className="bg-element-hover relative block h-1 w-full overflow-hidden rounded-full">
            <motion.span
              variants={segmentVariants}
              initial={reducedMotion ? false : 'empty'}
              animate={index <= current ? 'filled' : 'empty'}
              transition={reducedMotion ? segmentReducedMotionTransition : segmentTransition}
              className={twMerge('absolute inset-0 origin-left rounded-full', theme.accentDot)}
            />
          </span>
        )

        return interactive ? (
          <button
            key={index}
            type="button"
            aria-label={`Question ${index + 1}`}
            aria-current={index === current ? 'step' : undefined}
            disabled={!enabled}
            onClick={() => onSelect(index)}
            // Transparent explicitly: a base-layer rule gives every bare button
            // a grey background, which paints a band around the segment.
            className={twMerge(
              'relative h-2 flex-1 rounded-full bg-transparent hover:bg-transparent',
              enabled && 'cursor-pointer',
            )}
          >
            <span className="absolute inset-x-0 top-0.5">{segment}</span>
          </button>
        ) : (
          <span key={index} className="flex-1">
            {segment}
          </span>
        )
      })}
    </div>
  )
}
